import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Search, ShoppingCart, Pause } from 'lucide-react';
import { POSCartItem } from './POSCartItem';
import { fmtCurrency } from '../../lib/localeUtils';
import { POSCatalogHeading } from './POSCatalogHeading';
import { isPOSAvailable } from '../../lib/posAvailability';
import { InteractionBanner } from './POSCartPanel';

export const POSMobileView = ({
  search, onSearchChange, products, loading, onAddToCart,
  cart, total, cartOpen, onCartOpenChange,
  onUpdateQuantity, onQuantityInput, onQuantityBlur, onRemove,
  onHold, onCheckout,
  catalogTotal, catalogPage, onCatalogPageChange,
  interactionAlerts, interactionDismissed, onDismissInteractions,
}) => (
  <div className="md:hidden flex flex-col min-w-0 gap-3">
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        placeholder="Search medicines..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className="pl-10 h-11"
        data-testid="pos-mobile-search"
      />
    </div>

    <POSCatalogHeading mobile search={search} total={search.trim() ? catalogTotal : Math.min(15, catalogTotal)} page={catalogPage} onPageChange={onCatalogPageChange} />
    <div>
      {loading ? (
        <div className="grid grid-cols-2 gap-2">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : !products.some(product => isPOSAvailable(product)) ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="pos-mobile-no-products">
          <Search className="h-8 w-8 mb-2 opacity-40" />
          <p className="text-sm">{search.trim() ? 'No products found' : 'No frequently sold items yet'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2" data-testid="pos-mobile-product-grid">
          {products.filter(product => isPOSAvailable(product)).slice(0, 15).map((product) => {
            const oos = product.stock_quantity <= 0;
            const live = (product.batches || []).filter((b) => (b.quantity || 0) > 0);
            const recalled = product.recall_locked || (live.length > 0 && live.every((b) => b.recalled));
            return (
              <Card
                key={product.product_id}
                className={`min-w-0 cursor-pointer active:scale-[0.97] transition-transform ${oos || recalled ? 'opacity-50' : ''} ${recalled ? 'border-red-300' : ''}`}
                onClick={() => onAddToCart(product)}
                data-testid={`mobile-product-card-${product.product_id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-1.5">
                    <p className="text-sm font-semibold line-clamp-2 break-words" data-testid={`mobile-product-name-${product.product_id}`}>{product.name}</p>
                    {recalled && (
                      <Badge variant="destructive" className="text-[9px] shrink-0 px-1" data-testid={`mobile-product-card-recalled-${product.product_id}`}>RECALLED</Badge>
                    )}
                    {product.requires_prescription && (
                      <Badge variant="outline" className="text-[9px] shrink-0 px-1 border-blue-400 text-blue-600">Rx</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{product.generic_name || '—'}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm font-bold text-primary tabular-nums">{fmtCurrency(product.unit_price)}</span>
                    <span className={`text-[10px] ${oos ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                      {oos ? 'Out of stock' : `${product.stock_quantity} qty`}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>

    {cart.length > 0 && (
      <Button
        className="sticky bottom-3 z-10 w-full h-12 text-sm font-semibold gap-2 shadow-lg"
        onClick={() => onCartOpenChange(true)}
        data-testid="mobile-cart-btn"
      >
        <ShoppingCart className="h-4 w-4" />
        View Cart ({cart.length}) - {fmtCurrency(total)}
      </Button>
    )}

    <Dialog open={cartOpen} onOpenChange={onCartOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col" data-testid="mobile-cart-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" /> Cart ({cart.length} items)
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 min-h-0 max-h-[50vh]">
          <InteractionBanner alerts={interactionAlerts} dismissed={interactionDismissed} onDismiss={onDismissInteractions} testIdPrefix="mobile-pos" />
          <div className="space-y-3">
            {cart.map((item) => (
              <POSCartItem
                key={item.product_id}
                item={item}
                compact
                onUpdateQuantity={onUpdateQuantity}
                onQuantityInput={onQuantityInput}
                onQuantityBlur={onQuantityBlur}
                onRemove={onRemove}
              />
            ))}
          </div>
        </ScrollArea>
        <div className="border-t pt-3 space-y-3">
          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <span>{fmtCurrency(total)}</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="h-11 px-3 gap-1.5"
              onClick={onHold}
              data-testid="mobile-hold-sale-btn"
            >
              <Pause className="h-4 w-4" /> Hold
            </Button>
            <Button className="flex-1 h-11" onClick={onCheckout} data-testid="mobile-checkout-btn">
              Proceed to Checkout
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </div>
);
